/**
 * Register Page
 *
 * User registration with validation and age-based flow.
 */

import { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { FiEye, FiEyeOff, FiAlertCircle, FiInfo } from 'react-icons/fi'
import { FcGoogle } from 'react-icons/fc'
import { FaApple } from 'react-icons/fa'
import { useAuthStore } from '../../store'
import { calculateAge, isValidPassword, isValidEmail } from '../../api'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert'
import { Checkbox } from '../../components/ui/checkbox'

export function Register() {
  const navigate = useNavigate()
  const { register, isLoading, error, clearError } = useAuthStore()

  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    parentEmail: '', // For under-13 users
  })
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Check if user is under 13
  const age = formData.dateOfBirth ? calculateAge(formData.dateOfBirth) : null
  const isUnder13 = age !== null && age < 13

  // Validation
  const errors: Record<string, string> = {}

  if (touched.displayName && !formData.displayName) {
    errors.displayName = 'Display name is required'
  }
  if (touched.email) {
    if (!formData.email) {
      errors.email = 'Email is required'
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Invalid email format'
    }
  }
  if (touched.dateOfBirth && !formData.dateOfBirth) {
    errors.dateOfBirth = 'Date of birth is required'
  }
  if (touched.password) {
    const passwordCheck = isValidPassword(formData.password)
    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (!passwordCheck.valid) {
      errors.password = passwordCheck.message!
    }
  }
  if (touched.confirmPassword) {
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }
  }
  if (touched.acceptTerms && !formData.acceptTerms) {
    errors.acceptTerms = 'You must accept the terms'
  }
  if (isUnder13 && touched.parentEmail) {
    if (!formData.parentEmail) {
      errors.parentEmail = 'Parent email is required for users under 13'
    } else if (!isValidEmail(formData.parentEmail)) {
      errors.parentEmail = 'Invalid email format'
    }
  }

  // Clear error when form changes
  useEffect(() => {
    if (error) clearError()
  }, [formData, clearError, error])

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    // Mark all fields as touched
    setTouched({
      displayName: true,
      email: true,
      dateOfBirth: true,
      password: true,
      confirmPassword: true,
      acceptTerms: true,
      parentEmail: isUnder13,
    })

    // Check for validation errors
    if (Object.keys(errors).length > 0) return

    // Additional validation
    if (!formData.acceptTerms) return
    if (isUnder13 && !formData.parentEmail) return

    const success = await register({
      displayName: formData.displayName,
      email: formData.email,
      password: formData.password,
      dateOfBirth: formData.dateOfBirth,
      acceptTerms: formData.acceptTerms,
      parentEmail: isUnder13 ? formData.parentEmail : undefined,
    })

    if (success) {
      navigate('/onboarding', { replace: true })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8">
      <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-2xl font-bold text-foreground">
              Create your account
            </h1>
            <p className="text-foreground-muted">
              Join DoCurious and start your adventure
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <FiAlertCircle className="h-4 w-4" />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}

          {/* OAuth Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="w-full"
              disabled
              title="Google Sign-In is not yet configured"
            >
              <div className="flex items-center gap-2 opacity-50">
                <FcGoogle size={20} />
                <span>Continue with Google</span>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full"
              disabled
              title="Apple Sign-In is not yet configured"
            >
              <div className="flex items-center gap-2 opacity-50">
                <FaApple size={20} />
                <span>Continue with Apple</span>
              </div>
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-foreground-muted text-sm">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Under-13 Notice */}
          {isUnder13 && (
            <Alert>
              <FiInfo className="h-4 w-4" />
              <AlertTitle className="font-semibold">Under 13? No problem!</AlertTitle>
              <AlertDescription className="text-sm">
                We'll need a parent or guardian's email to complete your registration.
              </AlertDescription>
            </Alert>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              {/* Display Name */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Display Name</label>
                <Input
                  value={formData.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  onBlur={() => handleBlur('displayName')}
                  placeholder="How should we call you?"
                />
                {errors.displayName && <p className="text-sm text-error">{errors.displayName}</p>}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="you@example.com"
                />
                {errors.email && <p className="text-sm text-error">{errors.email}</p>}
              </div>

              {/* Date of Birth */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Date of Birth</label>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  onBlur={() => handleBlur('dateOfBirth')}
                />
                <p className="text-sm text-foreground-muted">
                  We use this for age-appropriate content
                </p>
                {errors.dateOfBirth && <p className="text-sm text-error">{errors.dateOfBirth}</p>}
              </div>

              {/* Parent Email (for under-13) */}
              {isUnder13 && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Parent/Guardian Email</label>
                  <Input
                    type="email"
                    value={formData.parentEmail}
                    onChange={(e) => handleChange('parentEmail', e.target.value)}
                    onBlur={() => handleBlur('parentEmail')}
                    placeholder="parent@example.com"
                  />
                  <p className="text-sm text-foreground-muted">
                    They'll receive an email to approve your account
                  </p>
                  {errors.parentEmail && <p className="text-sm text-error">{errors.parentEmail}</p>}
                </div>
              )}

              {/* Password */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    onBlur={() => handleBlur('password')}
                    placeholder="Create a strong password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-foreground-muted hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                <p className="text-sm text-foreground-muted">
                  8+ characters, uppercase, lowercase, and a number
                </p>
                {errors.password && <p className="text-sm text-error">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Confirm Password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  onBlur={() => handleBlur('confirmPassword')}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && <p className="text-sm text-error">{errors.confirmPassword}</p>}
              </div>

              {/* Terms Checkbox */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.acceptTerms}
                    onCheckedChange={(checked) => handleChange('acceptTerms', !!checked)}
                  />
                  <span className="text-sm text-foreground-muted">
                    I agree to the{' '}
                    <a href="/terms" target="_blank" className="text-primary hover:underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" target="_blank" className="text-primary hover:underline">
                      Privacy Policy
                    </a>
                  </span>
                </div>
                {errors.acceptTerms && <p className="text-sm text-error">{errors.acceptTerms}</p>}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </div>
          </form>

          {/* Login Link */}
          <p className="text-center text-foreground-muted">
            Already have an account?{' '}
            <RouterLink to="/login" className="text-primary hover:underline">
              Sign in
            </RouterLink>
          </p>
        </div>
      </div>
    </div>
  )
}
